import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useQueryClient } from 'react-query';
import { RenderIf } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import Badge from 'components/Badge';
import Button from 'components/Button';
import LoadingShape from 'components/LoadingShape';
import Close from 'components/icons/close';
import SearchIcon from 'components/icons/search';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import {
    useLinkNexhealthPatient,
    useNexhealthPatientCandidates,
    useNexhealthPatients,
} from '../hooks';
import { NexhealthPatient } from '../types';
import PatientItem from './patientItem';
import {
    CloseButton,
    EmptyText,
    Footer,
    Group,
    GroupTitle,
    Header,
    LoadMoreContainer,
    SearchInput,
    SkeletonAvatar,
    SkeletonItem,
    SkeletonLines,
    StyledDrawer,
    Title,
} from './styled';

const PatientSkeletons = ({ count }: { count: number }) => (
    <>
        {Array.from({ length: count }, (_, index) => (
            <SkeletonItem key={index}>
                <SkeletonAvatar shape="circle" width="2.5rem" height="2.5rem" />
                <SkeletonLines>
                    <LoadingShape shape="rounded-rect" width="50%" height="0.875rem" />
                    <LoadingShape shape="rounded-rect" width="75%" height="0.75rem" />
                </SkeletonLines>
            </SkeletonItem>
        ))}
    </>
);

interface LinkPatientDrawerProps {
    agentId: string;
    customerId: string;
    isOpen: boolean;
    onRequestClose: () => void;
}

const getPatientDetails = (patient: NexhealthPatient) => {
    const { bio } = patient;
    const dob = bio?.date_of_birth ? DateTime.fromISO(bio.date_of_birth) : undefined;
    return [
        dob?.isValid && `Born ${dob.toLocaleString(DateTime.DATE_MED)}`,
        formatIntlPhoneNumber(bio?.cell_phone_number || bio?.phone_number || ''),
        bio?.email || patient.email,
    ].filter(Boolean).join(' · ');
};

const LinkPatientDrawer = ({
    agentId,
    customerId,
    isOpen,
    onRequestClose,
}: LinkPatientDrawerProps) => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [cursor, setCursor] = useState<string>();
    const [patients, setPatients] = useState<NexhealthPatient[]>([]);
    const [selectedId, setSelectedId] = useState<number>();
    const isSearching = Boolean(search.trim());

    const {
        data: candidatesResult,
        isLoading: isLoadingCandidates,
    } = useNexhealthPatientCandidates(agentId, customerId, isOpen && !isSearching);
    const candidates = useMemo(
        () => (candidatesResult?.data || []).filter((candidate) => candidate.link.status === 'available'),
        [candidatesResult],
    );

    const {
        data: patientsResult,
        isLoading: isLoadingPatients,
        isFetching: isFetchingPatients,
    } = useNexhealthPatients({
        agentId,
        search,
        cursor,
        enabled: isOpen,
    });
    const nextCursor = patientsResult?.page?.nextCursor;

    useEffect(() => {
        if (!patientsResult) return;
        setPatients((current) => {
            if (!cursor) return patientsResult.data;
            const ids = new Set(current.map((patient) => patient.id));
            return [...current, ...patientsResult.data.filter((patient) => !ids.has(patient.id))];
        });
    }, [patientsResult, cursor]);

    useEffect(() => {
        setCursor(undefined);
    }, [search]);

    useEffect(() => {
        if (isOpen) return;
        setSearch('');
        setCursor(undefined);
        setSelectedId(undefined);
    }, [isOpen]);

    const showSuggested = !isSearching && (isLoadingCandidates || candidates.length > 0);
    let patientsTitle = 'Patients';
    if (isSearching) patientsTitle = 'Search results';
    else if (showSuggested) patientsTitle = 'Other patients';

    const otherPatients = useMemo(() => {
        if (isSearching) return patients;
        const candidateIds = new Set(candidates.map((candidate) => candidate.patient.id));
        return patients.filter((patient) => !candidateIds.has(patient.id));
    }, [candidates, isSearching, patients]);

    const { mutateAsync: linkPatient, isLoading: isLinking } = useLinkNexhealthPatient(
        agentId,
        customerId,
    );

    const handleLink = async () => {
        if (!selectedId) return;
        try {
            await linkPatient({ body: { customerId, patientId: selectedId } });
            onRequestClose();
        } catch (error) {
            const code = (error as any)?.code || (error as any)?.error?.code;
            if (code === 'PATIENT_LINK_EXISTS') {
                queryClient.invalidateQueries(['nexhealth', 'patient-link', agentId, customerId]);
                onRequestClose();
                return;
            }
            showAppNotification({
                title: 'Error',
                description: (error as any)?.message || 'Something went wrong. Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
    };

    return (
        <StyledDrawer
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            slideFrom="right"
            hideCloseButton
            header={(
                <Header>
                    <Title>Link patient</Title>
                    <CloseButton icon={<Close />} onClick={onRequestClose} />
                    <SearchInput
                        type="search"
                        placeholder="Search by name, phone, or email"
                        icon={<SearchIcon />}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </Header>
            )}
            footer={(
                <Footer>
                    <Button
                        variant="border"
                        borderRadius="semi-rounded"
                        label="Cancel"
                        onClick={onRequestClose}
                    />
                    <Button
                        variant="brand"
                        borderRadius="semi-rounded"
                        label="Link patient"
                        disabled={!selectedId || isLinking}
                        isLoading={isLinking}
                        onClick={handleLink}
                    />
                </Footer>
            )}
        >
            <RenderIf isTrue={showSuggested}>
                <Group>
                    <GroupTitle>Suggested</GroupTitle>
                    <RenderIf isTrue={isLoadingCandidates}>
                        <PatientSkeletons count={2} />
                    </RenderIf>
                    {candidates.map((candidate) => (
                        <PatientItem
                            key={candidate.patient.id}
                            name={candidate.patient.name}
                            firstName={candidate.patient.firstName}
                            lastName={candidate.patient.lastName}
                            details={candidate.matchedOn.length > 0 ? `Matched on ${candidate.matchedOn.join(', ')}` : undefined}
                            inactive={candidate.patient.inactive}
                            badge={(
                                <Badge
                                    label={`${Math.round(candidate.probability * 100)}% match`}
                                    variant="brand"
                                    size="small"
                                />
                            )}
                            isSelected={selectedId === candidate.patient.id}
                            onClick={() => setSelectedId(candidate.patient.id)}
                        />
                    ))}
                </Group>
            </RenderIf>
            <Group>
                <GroupTitle>{patientsTitle}</GroupTitle>
                <RenderIf isTrue={isLoadingPatients || (isFetchingPatients && !cursor)}>
                    <PatientSkeletons count={4} />
                </RenderIf>
                <RenderIf isTrue={!isFetchingPatients && otherPatients.length === 0}>
                    <EmptyText>
                        {isSearching ? 'No patients match your search.' : 'No patients found.'}
                    </EmptyText>
                </RenderIf>
                <RenderIf isTrue={!(isFetchingPatients && !cursor)}>
                    {otherPatients.map((patient) => (
                        <PatientItem
                            key={patient.id}
                            name={patient.name || [patient.first_name, patient.last_name].filter(Boolean).join(' ')}
                            firstName={patient.first_name}
                            lastName={patient.last_name}
                            details={getPatientDetails(patient)}
                            inactive={patient.inactive}
                            isSelected={selectedId === patient.id}
                            onClick={() => setSelectedId(patient.id)}
                        />
                    ))}
                </RenderIf>
                <RenderIf isTrue={nextCursor}>
                    <LoadMoreContainer>
                        <Button
                            variant="base"
                            borderRadius="semi-rounded"
                            label="Load more"
                            isLoading={isFetchingPatients && Boolean(cursor)}
                            onClick={() => setCursor(nextCursor as string)}
                        />
                    </LoadMoreContainer>
                </RenderIf>
            </Group>
        </StyledDrawer>
    );
};

export default LinkPatientDrawer;
